/**
 * @name SaldoPage
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Página principal de créditos - centro de gestão com modais de recarga e histórico
 * @package components/pages/gestao-financeira/creditos
 */

import { History, Info as InfoIcon, Pix as PixIcon, Refresh as RefreshIcon, TrendingUp, AccountBalanceWallet as WalletIcon, Build as BuildIcon } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import PageContainer from '../../../layouts/PageContainer';
import ModalHistorico from './components/ModalHistorico';
import ModalRecarga from './components/ModalRecarga';
import { useCreditosData } from './useCreditosData';

/**
 * Página principal de gestão de créditos
 * Centro de controle com modais para recarga via PIX e histórico
 * Consome dados da view vw_saldos_responsaveis via hook database-driven
 */
const SaldoPage: React.FC = () => {
     // 🚧 FLAG DE MANUTENÇÃO - Alterar para true para desativar recargas temporariamente
     const RECARGA_EM_MANUTENCAO = false;
     const { saldo, movimentacoes, loading, recarregando, recarregar, metricas, metadados } = useCreditosData();

     // Estados para modais
     const [openModalRecarga, setOpenModalRecarga] = useState(false);
     const [openModalHistorico, setOpenModalHistorico] = useState(false);
     const [recargaComValor, setRecargaComValor] = useState<{ valor?: number; descricao?: string } | null>(null);

     // Breadcrumbs para navegação
     const breadcrumbs = metadados.breadcrumbs;

     // Ações do cabeçalho
     const actions = (
          <>
               <Button
                    variant='outlined'
                    startIcon={<RefreshIcon />}
                    onClick={recarregar}
                    disabled={recarregando}
               >
                    {recarregando ? 'Atualizando...' : 'Atualizar'}
               </Button>
               <Button
                    variant='outlined'
                    startIcon={<History />}
                    onClick={() => setOpenModalHistorico(true)}
               >
                    Histórico
               </Button>
               <Button
                    variant='contained'
                    startIcon={RECARGA_EM_MANUTENCAO ? <BuildIcon /> : <PixIcon />}
                    color={RECARGA_EM_MANUTENCAO ? 'warning' : 'secondary'}
                    onClick={() => setOpenModalRecarga(true)}
                    disabled={RECARGA_EM_MANUTENCAO}
               >
                    {RECARGA_EM_MANUTENCAO ? 'Em Manutenção' : 'Recarregar via PIX'}
               </Button>
          </>
     );

     // Handlers para ações rápidas - agora abre diretamente o modal de recarga
     const handleAcaoRapida = (valor: number) => {
          setRecargaComValor({
               valor: valor,
               descricao: `Recarga rápida de R$ ${valor.toFixed(2)}`,
          });
          setOpenModalRecarga(true);
     };

     const handleFecharModalRecarga = () => {
          setOpenModalRecarga(false);
          setRecargaComValor(null);
     };

     if (loading) {
          return (
               <PageContainer
                    title={metadados.titulo}
                    breadcrumbs={breadcrumbs}
                    actions={actions}
                    icon={<WalletIcon />}
               >
                    <Box
                         display='flex'
                         justifyContent='center'
                         alignItems='center'
                         minHeight='400px'
                    >
                         <CircularProgress />
                    </Box>
               </PageContainer>
          );
     }

     return (
          <PageContainer
               title={metadados.titulo}
               breadcrumbs={breadcrumbs}
               actions={actions}
               icon={<WalletIcon />}
          >
               {/* Aviso de Manutenção */}
               {RECARGA_EM_MANUTENCAO && (
                    <Alert
                         severity='warning'
                         icon={<BuildIcon />}
                         sx={{ mb: 3 }}
                    >
                         <Typography variant='body1' 
                              fontWeight='bold' 
                              gutterBottom 
                              sx={{
                                   textShadow: '0 2px 8px rgba(0,0,0,0.25)', 
                                   color: '#1f1f1f'}}
                         >
                              🚧 Funcionalidade de Recarga Temporariamente Indisponível
                         </Typography>
                         <Typography 
                              variant='body2' 
                              sx={{
                                   textShadow: '0 1px 4px rgba(0,0,0,0.15)'
                              }}
                         >
                              A recarga de créditos via PIX está em manutenção no momento. Você ainda pode visualizar seu saldo, histórico e transferir créditos para dependentes. Em breve esta funcionalidade estará disponível novamente.
                         </Typography>
                    </Alert>
               )}

               {/* Aviso amigável sobre o tempo de compensação dos créditos */}
               {!RECARGA_EM_MANUTENCAO && (
                    <Alert
                         severity='info'
                         icon={<InfoIcon />}
                         sx={{ mb: 3,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                          }}
                    >
                         <Typography 
                              variant='body2'
                              sx={{
                              textShadow: '0 1px 4px rgba(0,0,0,0.15)'
                              }}
                         >
                              Após a confirmação do pagamento via PIX, seus créditos costumam cair em até <strong>30 minutos</strong>. Se não aparecerem nesse prazo, clique em <strong>Atualizar</strong> ou confira o <strong>Histórico</strong> antes de falar com o suporte.
                         </Typography>
                    </Alert>
               )}

               <Grid
                    container
                    spacing={3}
               >
                    {/* Card Principal de Saldo */}
                    <Grid
                         item
                         xs={12}
                         md={8}
                    >
                         <Card elevation={2}>
                              <CardContent sx={{ p: 3 }}>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Saldo Disponível
                                   </Typography>
                                   <Typography
                                        variant='h3'
                                        color='primary'
                                        gutterBottom
                                        sx={{ fontWeight: 'bold' }}
                                   >
                                        R$ {metricas.saldoDisponivel.toFixed(2)}
                                   </Typography>

                                   {saldo && (
                                        <Box sx={{ mt: 2 }}>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Responsável: {saldo.responsavel}
                                             </Typography>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Email: {saldo.email}
                                             </Typography>
                                             {metricas.saldoBloqueado > 0 && (
                                                  <Typography
                                                       variant='body2'
                                                       color='warning.main'
                                                       sx={{ mt: 1 }}
                                                  >
                                                       Saldo Bloqueado: R$ {metricas.saldoBloqueado.toFixed(2)}
                                                  </Typography>
                                             )}
                                        </Box>
                                   )}
                              </CardContent>
                         </Card>
                    </Grid>

                    {/* Ações Rápidas de Recarga */}
                    <Grid
                         item
                         xs={12}
                         md={4}
                    >
                         <Card elevation={2}>
                              <CardContent sx={{ p: 3 }}>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Ações Rápidas
                                   </Typography>
                                   <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mb: 2 }}
                                   >
                                        Recarga via PIX
                                   </Typography>
                                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Button
                                             variant='contained'
                                             fullWidth
                                             color={RECARGA_EM_MANUTENCAO ? 'warning' : 'secondary'}
                                             startIcon={RECARGA_EM_MANUTENCAO ? <BuildIcon /> : <PixIcon />}
                                             onClick={() => handleAcaoRapida(50)}
                                             disabled={RECARGA_EM_MANUTENCAO}
                                        >
                                             {RECARGA_EM_MANUTENCAO ? 'Em Manutenção' : 'PIX R$ 50,00'}
                                        </Button>
                                        <Button
                                             variant='outlined'
                                             fullWidth
                                             startIcon={RECARGA_EM_MANUTENCAO ? <BuildIcon /> : <PixIcon />}
                                             onClick={() => handleAcaoRapida(100)}
                                             disabled={RECARGA_EM_MANUTENCAO}
                                        >
                                             {RECARGA_EM_MANUTENCAO ? 'Em Manutenção' : 'PIX R$ 100,00'}
                                        </Button>
                                        <Button
                                             variant='outlined'
                                             fullWidth
                                             onClick={() => setOpenModalRecarga(true)}
                                             disabled={RECARGA_EM_MANUTENCAO}
                                        >
                                             {RECARGA_EM_MANUTENCAO ? 'Em Manutenção' : 'Valor Personalizado'}
                                        </Button>
                                   </Box>
                              </CardContent>
                         </Card>
                    </Grid>

                    {/* Estatísticas Rápidas */}
                    <Grid
                         item
                         xs={12}
                    >
                         <Paper
                              elevation={2}
                              sx={{
                                   p: 3,
                                   background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                   color: 'white',
                              }}
                         >
                              <Typography
                                   variant='h6'
                                   gutterBottom
                                   sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                   <TrendingUp />
                                   Resumo do Mês
                              </Typography>

                              <Grid
                                   container
                                   spacing={3}
                              >
                                   <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                   >
                                        <Typography
                                             variant='body2'
                                             sx={{ opacity: 0.9 }}
                                        >
                                             Dependentes Cadastrados
                                        </Typography>
                                        <Typography
                                             variant='h4'
                                             sx={{ fontWeight: 'bold' }}
                                        >
                                             {metricas.totalDependentes}
                                        </Typography>
                                   </Grid>

                                   <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                   >
                                        <Typography
                                             variant='body2'
                                             sx={{ opacity: 0.9 }}
                                        >
                                             Movimentações no Mês
                                        </Typography>
                                        <Typography
                                             variant='h4'
                                             sx={{ fontWeight: 'bold' }}
                                        >
                                             {metricas.totalMovimentacoesMes}
                                        </Typography>
                                   </Grid>

                                   <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                   >
                                        <Typography
                                             variant='body2'
                                             sx={{ opacity: 0.9 }}
                                        >
                                             Última Movimentação
                                        </Typography>
                                        <Typography
                                             variant='h6'
                                             sx={{ fontWeight: 'bold' }}
                                        >
                                             {metricas.ultimaMovimentacao ? new Date(metricas.ultimaMovimentacao.data_movimentacao!).toLocaleDateString() : 'Nenhuma'}
                                        </Typography>
                                   </Grid>
                              </Grid>
                         </Paper>
                    </Grid>

                    {/* Últimas Movimentações */}
                    <Grid
                         item
                         xs={12}
                    >
                         <Card elevation={2}>
                              <CardContent sx={{ p: 3 }}>
                                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography
                                             variant='h6'
                                             sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                        >
                                             <History />
                                             Últimas Movimentações
                                        </Typography>
                                        <Button
                                             variant='outlined'
                                             size='small'
                                             onClick={() => setOpenModalHistorico(true)}
                                        >
                                             Ver Todas
                                        </Button>
                                   </Box>

                                   {movimentacoes.length === 0 ? (
                                        <Typography
                                             variant='body2'
                                             color='text.secondary'
                                        >
                                             Nenhuma movimentação encontrada
                                        </Typography>
                                   ) : (
                                        <Box>
                                             {movimentacoes.slice(0, 5).map((movimentacao, index) => (
                                                  <Box
                                                       key={movimentacao.id || index}
                                                       sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            py: 2,
                                                            borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none',
                                                       }}
                                                  >
                                                       <Box>
                                                            <Typography
                                                                 variant='body1'
                                                                 fontWeight='medium'
                                                            >
                                                                 {movimentacao.descricao}
                                                            </Typography>
                                                            <Typography
                                                                 variant='body2'
                                                                 color='text.secondary'
                                                            >
                                                                 {movimentacao.data_movimentacao ? new Date(movimentacao.data_movimentacao).toLocaleDateString() : 'Data não disponível'}
                                                            </Typography>
                                                       </Box>
                                                       <Typography
                                                            variant='h6'
                                                            color={movimentacao.tipo_movimentacao === 'credito' ? 'success.main' : 'error.main'}
                                                            fontWeight='bold'
                                                       >
                                                            {movimentacao.tipo_movimentacao === 'credito' ? '+' : '-'}
                                                            R$ {(movimentacao.valor || 0).toFixed(2)}
                                                       </Typography>
                                                  </Box>
                                             ))}
                                        </Box>
                                   )}
                              </CardContent>
                         </Card>
                    </Grid>
               </Grid>

               {/* Modal de Recarga via PIX */}
               <ModalRecarga
                    open={openModalRecarga}
                    onClose={handleFecharModalRecarga}
                    onSuccess={() => {
                         // Recarregar dados após sucesso
                         recarregar();
                    }}
                    valorInicial={recargaComValor?.valor}
                    descricaoInicial={recargaComValor?.descricao}
               />

               {/* Modal de Histórico */}
               <ModalHistorico
                    open={openModalHistorico}
                    onClose={() => setOpenModalHistorico(false)}
               />
          </PageContainer>
     );
};

export default SaldoPage;
